<template>
    <div class="lms__superadmin_workspaces_edit_certificates">
        <component-title
            :class="'lms__superadmin_workspaces_edit_certificates_title'"
            :size="'large'"
        >Certificate templates
        </component-title>
        <component-list
            ref="certificateslist"
            :class="'lms__superadmin_workspaces_edit_certificates_list'"
            :component-table="LmsSuperadminWorkspaceEditCertificatesTable"
            :component-table-name="'certificates'"
            :get-items-getter="'getEbsCertificatesData'"
            :get-items-dispatch="workspaceId ? 'ebsCertificatesGet' : ''"
            :get-items-dispatch-data="{ workspaceId }"
            :item-name="['certificate', 'certificates']"
            :filter-actions-active="true"
            :filter-actions-primary-button-text="'Create new template'"
            :filter-actions-primary-button-active="true"
            @emit-actions-primary="handleAddCertificate"
            @emit-preview="handlePreviewCertificateByContentId"
            @emit-set-default="handleSetDefaultCertificate"
            @emit-unset-default="handleUnsetDefaultCertificate"
            @emit-edit="handleEditCertificate"
            @emit-delete="handleDeleteCertifitate"
        ></component-list>
        <pop-notify
            ref="notifyeditcertificate"
            :class="'pop__notify--edit'"
            :title-text="popNotifyProcess === 'add' ? 'Add certificate' : 'Edit certificate'"
            :content-text="''"
            :cancel-button-active="false"
            :submit-button-active="false"
            :size="'big'"
        >
            <template v-if="getCertificateRequestPending">
                <component-loading :size="'small'"></component-loading>
            </template>
            <template v-else>
                <form-text
                    :class="'lms__superadmin_workspaces_edit_certificates_custom_name'"
                    :name="'lms-superadmin-workspace-edit-certificates-name'"
                    :label="'Template name'"
                    :required="true"
                    :v-validate="{ required: true }"
                    :data-vv-as="'template name'"
                    v-model="certificateName"
                ></form-text>
                <form-checkbox
                    :class="'lms__superadmin_workspaces_edit_certificates_default'"
                    :name="'lms-superadmin-workspace-edit-certificates-default'"
                    :label="'Make it workspace default'"
                    v-model="certificateWorkspaceDefault"
                ></form-checkbox>
                <component-wrapper :class="'lms__superadmin_workspaces_edit_certificates_header'">
                    <form-image
                        :class="'lms__superadmin_workspaces_edit_certificates_header_id'"
                        :label="'Header image'"
                        :name="'lms-superadmin-workspace-edit-certificates-header-id'"
                        :customer-id="workspaceCustomerId"
                        :value="certificateLogoImage"
                        @emit-select="certificateLogoUploadImage"
                        @emit-remove="certificateLogoRemoveImage"
                    ></form-image>
                    <form-radio-horizontal
                        :class="'lms__superadmin_workspaces_edit_certificates_header_position'"
                        :label="'Header position'"
                        :name="'lms-superadmin-workspace-edit-certificates-header-position'"
                        :items="[
                            {
                                name: 'Left',
                                value: 'left'
                            },
                            {
                                name: 'Center',
                                value: 'center'
                            },
                            {
                                name: 'Right',
                                value: 'right'
                            }
                        ]"
                        v-model="certificateLogoPosition"
                    ></form-radio-horizontal>
                </component-wrapper>
                <form-editor
                    :class="'lms__superadmin_workspaces_edit_certificates_content'"
                    :label="'Content'"
                    :name="'lms-superadmin-workspace-edit-certificates-content'"
                    :tags="tagsContent"
                    v-model="certificateHtml"
                ></form-editor>
                <component-wrapper :class="'lms__superadmin_workspaces_edit_certificates_footer'">
                    <form-image
                        :class="'lms__superadmin_workspaces_edit_certificates_footer_id'"
                        :label="'Footer'"
                        :name="'lms-superadmin-workspace-edit-certificates-footer'"
                        :customer-id="workspaceCustomerId"
                        :value="certificateFooterImage"
                        @emit-select="certificateFooterUploadImage"
                        @emit-remove="certificateFooterRemoveImage"
                    ></form-image>
                    <form-radio-horizontal
                        :class="'lms__superadmin_workspaces_edit_certificates_footer_position'"
                        :label="'Footer position'"
                        :name="'lms-superadmin-workspace-edit-certificates-footer-position'"
                        :items="[
                        {
                            name: 'Left',
                            value: 'left'
                        },
                        {
                            name: 'Center',
                            value: 'center'
                        },
                        {
                            name: 'Right',
                            value: 'right'
                        }
                    ]"
                        v-model="certificateFooterPosition"
                    ></form-radio-horizontal>
                </component-wrapper>
                <div class="columns">
                    <div class="column"></div>
                    <div class="column is-narrow">
                        <component-button
                            :name="'lms-superadmin-workspace-edit-certificates-preview'"
                            :theme="'default'"
                            @click="handlePreviewCertificate"
                        >Preview
                        </component-button>
                    </div>
                    <div class="column is-narrow">
                        <component-button
                            :name="'lms-superadmin-workspace-edit-certificates-submit'"
                            :animate-loading="true"
                            :animate-loading-event-name="'certificate-submit'"
                            @click="handleSubmitCertificate"
                        >Submit
                        </component-button>
                    </div>
                </div>
            </template>
        </pop-notify>
        <pop-notify
            ref="notifyremovecertificate"
            :class="'pop__notify--remove'"
            :title-text="'Remove certificate'"
            :content-text="'Are you sure want to remove this certificate?'"
            :cancel-button-text="'Cancel'"
            :submit-button-text="'Confirm'"
            :submit-button-danger="true"
            :submit-button-animate-loading="true"
            @emit-submit="performDeleteCertificate"
            @emit-close="() => popNotifyRerenderIndex++"
        />
    </div>
</template>

<script>
import { performReset, performSubmitValidate, validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import { User } from '@/User'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import ComponentButton from '@/components/default/shared/ComponentButton'
import FormText from '@/components/default/forms/FormText'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import ComponentWrapper from '@/components/default/shared/ComponentWrapper'
import FormEditor from '@/components/default/forms/FormEditor'
import FormImage from '@/components/default/forms/FormImage'
import ComponentLoading from '@/components/default/shared/ComponentLoading'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentList from '@/components/default/shared/ComponentList'
import LmsSuperadminWorkspaceEditCertificatesTable from '@/components/lms/superadmin/workspace/edit/LmsSuperadminWorkspaceEditCertificatesTable'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'

export default {
  name: 'lms-superadmin-workspace-edit-certificates',
  components: { FormRadioHorizontal, ComponentList, PopNotify, ComponentLoading, FormImage, FormEditor, ComponentWrapper, FormCheckbox, FormText, ComponentButton, ComponentTitle },
  mixins: [validateBeforeRouteLeave, performReset, performSubmitValidate, watchContentChangeEmit],
  data () {
    return {
      user: new User(),
      getCertificateRequestPending: false,
      popNotifyRerenderIndex: 0,
      popNotifyProcess: 'add',
      LmsSuperadminWorkspaceEditCertificatesTable
    }
  },
  computed: {
    workspaceId () {
      return this.$route.params.id
    },
    workspaceCustomerId () {
      return this.$store.getters.getLmsSuperadminWorkspaceCustomerId
    },
    workspaceCertificateId () {
      return this.$store.getters.getLmsSuperadminWorkspaceCertificateId
    },
    workspaceCertificateType () {
      return this.$store.getters.getLmsSuperadminWorkspaceCertificateType
    },
    certificateWorkspaceDefault: {
      get () {
        return this.$store.getters.getEbsCertificateWorkspaceDefault
      },
      set (value) {
        this.$store.dispatch('ebsCertificateCheckWorkspaceDefault', value)
      }
    },
    certificateId: {
      get () {
        return this.$store.getters.getEbsCertificateId
      }
    },
    certificateName: {
      get () {
        return this.$store.getters.getEbsCertificateName
      },
      set (value) {
        this.$store.dispatch('ebsCertificateSetName', value)
      }
    },
    certificateHtml: {
      get () {
        return this.$store.getters.getEbsCertificateHtml
      },
      set (value) {
        this.$store.dispatch('ebsCertificateSetHtml', value)
      }
    },
    certificatePreview: {
      get () {
        return this.$store.getters.getEbsCertificatePreview
      }
    },
    certificateLogoImage: {
      get () {
        return this.$store.getters.getEbsCertificateLogoImage
      }
    },
    certificateLogoPosition: {
      get () {
        return this.$store.getters.getEbsCertificateLogoPosition
      },
      set (value) {
        this.$store.dispatch('ebsCertificateSetDefaultsLogoPosition', value)
      }
    },
    certificateFooterImage: {
      get () {
        return this.$store.getters.getEbsCertificateFooterImage
      }
    },
    certificateFooterPosition: {
      get () {
        return this.$store.getters.getEbsCertificateFooterPosition
      },
      set (value) {
        this.$store.dispatch('ebsCertificateSetDefaultsFooterPosition', value)
      }
    },
    tagsContent () {
      return this.$store.getters.getTagsContentMapped
    }
  },
  methods: {
    getTagsContent () {
      this.$store.dispatch('tagsContentGet')
    },
    getCertificate (value) {
      this.getCertificateRequestPending = true
      this.$store.dispatch('ebsCertificateGet', { certificateId: value, workspaceId: this.workspaceId }).finally(() => {
        this.getCertificateRequestPending = false
      })
    },
    certificateLogoUploadImage (data) {
      this.$store.dispatch('ebsCertificateSetDefaultsLogoId', data)
    },
    certificateLogoRemoveImage () {
      this.$store.dispatch('ebsCertificateRemoveDefaultsLogo')
    },
    certificateFooterUploadImage (data) {
      this.$store.dispatch('ebsCertificateSetDefaultsFooterId', data)
    },
    certificateFooterRemoveImage () {
      this.$store.dispatch('ebsCertificateRemoveDefaultsFooter')
    },
    clearWorkspaceCertificate () {
      this.$store.dispatch('lmsSuperadminWorkspaceClearCertificate')
    },
    clearCertificate () {
      this.popNotifyRerenderIndex++
      this.$store.dispatch('ebsCertificateClear')
    },
    handleSubmitCertificate () {
      let dispatchName

      if (this.popNotifyProcess === 'add') {
        dispatchName = 'ebsCertificateCreate'
      } else {
        dispatchName = 'ebsCertificateUpdate'
      }

      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$store.dispatch(dispatchName, { workspaceId: this.workspaceId, workspaceDefault: this.certificateWorkspaceDefault }).then(() => {
            this.$refs.notifyeditcertificate.closeNotify()
            this.$refs.certificateslist.getItems()
          })
        } else {
          this.$eventBus.$emit('stop-certificate-submit')
        }
      })
    },
    handleSetDefaultCertificate (data) {
      this.$store.dispatch('ebsCertificateSetWorkspaceDefault', data).then(() => {
        this.$refs.certificateslist.getItems()
      })
    },
    handleUnsetDefaultCertificate (data) {
      this.$store.dispatch('ebsCertificateUnsetWorkspaceDefault', data).then(() => {
        this.$refs.certificateslist.getItems()
      })
    },
    handlePreviewCertificate (data) {
      this.$store.dispatch('ebsCertificatePreviewByCertificateId', data)
    },
    handlePreviewCertificateByContentId (data) {
      this.$store.dispatch('ebsCertificatePreviewByContentId', data)
    },
    handleAddCertificate () {
      this.clearCertificate()
      this.getTagsContent()
      this.popNotifyProcess = 'add'
      this.$refs.notifyeditcertificate.openNotify()
    },
    handleEditCertificate (data) {
      this.popNotifyProcess = 'edit'
      this.clearCertificate()
      this.getTagsContent()
      this.getCertificate(data)
      this.$refs.notifyeditcertificate.openNotify()
    },
    handleDeleteCertifitate (data) {
      this.$refs.notifyremovecertificate.setStoreValue(data)
      this.$refs.notifyremovecertificate.openNotify()
    },
    performDeleteCertificate () {
      const certificateId = this.$refs.notifyremovecertificate.storeValue
      this.$store.dispatch('ebsCertificateRemove', certificateId).then(() => {
        this.$refs.certificateslist.getItems()
      }).finally(() => {
        this.$refs.notifyremovecertificate.closeNotify()
      })
    }
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/workspaces/_lmssuperadminworkspacesaddeditcertificates.scss";
</style>
